import type { GridState } from 'ag-grid-community';
import { EMPTY_OBJECT } from '../../utils';
import type { BlotterTableState, ExtraGridState } from './BlotterTableStorageContext';

export type UsePersistedBlotterTableOptions = {
  /** When false, persisted filter is ignored and filter changes are not written to storage. Default true. */
  persistFilter?: boolean;
  /** When false, persisted grid state is ignored and grid state changes are not written to storage. Default true. */
  persistGridState?: boolean;
  /** When false, all column-state keys are dropped from persisted/returned grid state. Default true. */
  persistColumns?: boolean;
  /** When false, focused cell and cell-range selection are dropped from persisted/returned grid state. Default false. */
  persistCellSelection?: boolean;
  /** When false, row selection is dropped from persisted/returned grid state. Default false. */
  persistRowSelection?: boolean;
  /** When false, row-group expansion intent/exceptions are not persisted or returned. Default true. */
  persistRowGroupExpansion?: boolean;
  /** When false, scroll state is dropped from persisted/returned grid state. Default true. */
  persistScroll?: boolean;
};

export type DefaultedUsePersistedBlotterTableOptions = Required<UsePersistedBlotterTableOptions>;

const DEFAULT_PERSISTED_BLOTTER_TABLE_OPTIONS: DefaultedUsePersistedBlotterTableOptions = {
  persistFilter: true,
  persistGridState: true,
  persistColumns: true,
  persistCellSelection: false,
  persistRowSelection: false,
  persistRowGroupExpansion: true,
  persistScroll: false,
};

const NON_PERSISTED_GRID_STATE_KEYS = ['aggregation'] as const satisfies ReadonlyArray<keyof GridState>;
const COLUMN_STATE_KEYS = [
  'columnGroup',
  'columnOrder',
  'columnPinning',
  'columnSizing',
  'columnVisibility',
  'pivot',
  'rowGroup',
  'sort',
  'partialColumnState',
] as const satisfies ReadonlyArray<keyof GridState>;

const CELL_SELECTION_STATE_KEYS = ['focusedCell', 'cellSelection', 'rangeSelection'] as const satisfies ReadonlyArray<
  keyof GridState
>;
const ROW_SELECTION_STATE_KEYS = ['rowSelection'] as const satisfies ReadonlyArray<keyof GridState>;
const SCROLL_STATE_KEYS = ['scroll'] as const satisfies ReadonlyArray<keyof GridState>;

/** Resolves persistence options with defaults so callers can pass around one complete options object. */
export function getDefaultedOptions(
  inputOptions: UsePersistedBlotterTableOptions = EMPTY_OBJECT
): DefaultedUsePersistedBlotterTableOptions {
  const {
    persistFilter,
    persistGridState,
    persistColumns,
    persistCellSelection,
    persistRowSelection,
    persistRowGroupExpansion,
    persistScroll,
  } = inputOptions;

  return {
    persistFilter: persistFilter ?? DEFAULT_PERSISTED_BLOTTER_TABLE_OPTIONS.persistFilter,
    persistGridState: persistGridState ?? DEFAULT_PERSISTED_BLOTTER_TABLE_OPTIONS.persistGridState,
    persistColumns: persistColumns ?? DEFAULT_PERSISTED_BLOTTER_TABLE_OPTIONS.persistColumns,
    persistCellSelection: persistCellSelection ?? DEFAULT_PERSISTED_BLOTTER_TABLE_OPTIONS.persistCellSelection,
    persistRowSelection: persistRowSelection ?? DEFAULT_PERSISTED_BLOTTER_TABLE_OPTIONS.persistRowSelection,
    persistRowGroupExpansion:
      persistRowGroupExpansion ?? DEFAULT_PERSISTED_BLOTTER_TABLE_OPTIONS.persistRowGroupExpansion,
    persistScroll: persistScroll ?? DEFAULT_PERSISTED_BLOTTER_TABLE_OPTIONS.persistScroll,
  };
}

/** Produces the grid-state shape used for both persisted writes and persisted reads. */
export function filterGridStateForReadWrite(
  gridState: BlotterTableState['gridState'],
  options: DefaultedUsePersistedBlotterTableOptions
): BlotterTableState['gridState'] {
  if (!options.persistGridState || gridState == null) {
    return undefined;
  }

  const filteredState: GridState = { ...gridState };

  // Aggregation state can contain custom aggFunc references, which AG Grid cannot safely serialize/restore.
  removeGridStateKeys(filteredState, NON_PERSISTED_GRID_STATE_KEYS);

  if (!options.persistColumns) {
    removeGridStateKeys(filteredState, COLUMN_STATE_KEYS);
  }

  if (!options.persistCellSelection) {
    removeGridStateKeys(filteredState, CELL_SELECTION_STATE_KEYS);
  }

  if (!options.persistRowSelection) {
    removeGridStateKeys(filteredState, ROW_SELECTION_STATE_KEYS);
  }

  if (!options.persistScroll) {
    removeGridStateKeys(filteredState, SCROLL_STATE_KEYS);
  }

  return filteredState;
}

/** Produces the extra-grid-state shape used for both persisted writes and persisted reads. */
export function filterExtraGridStateForReadWrite(
  extraGridState: BlotterTableState['extraGridState'],
  options: DefaultedUsePersistedBlotterTableOptions
): BlotterTableState['extraGridState'] {
  if (!options.persistGridState || extraGridState == null) {
    return undefined;
  }

  const filteredState: ExtraGridState = { ...extraGridState };

  if (!options.persistRowGroupExpansion) {
    delete filteredState.rowGroupExpansion;
  }

  return Object.keys(filteredState).length > 0 ? filteredState : undefined;
}

function removeGridStateKeys(targetState: GridState, keys: ReadonlyArray<keyof GridState>) {
  for (const key of keys) {
    delete targetState[key];
  }
}
